import { useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useFullProfile } from '@/hooks/useFullProfile';
import { toast } from '@/hooks/useToast';
import { supabase } from '@/integrations/supabase/client';
import { EdgeFunctionError, invokeEdgeFunction } from '@/utils/supabase-functions';
import type { TranslationKey } from '@/types/i18n.types';

/**
 * Codici errore emessi dalle edge function stripe-connect-* → chiave i18n.
 * I codici sono definiti in supabase/functions/common/stripe-connect.ts
 * (`StripeErrorCode`): tenere le due liste allineate.
 * Un codice non mappato ricade sul messaggio generico.
 */
const ERROR_KEYS: Record<string, TranslationKey> = {
  account_already_linked: 'agency.stripe.errors.accountAlreadyLinked',
  no_stripe_account: 'agency.stripe.errors.noAccount',
  onboarding_incomplete: 'agency.stripe.errors.onboardingIncomplete',
  stripe_not_configured: 'agency.stripe.errors.notConfigured',
  stripe_client_id_missing: 'agency.stripe.errors.notConfigured',
  missing_oauth_code: 'agency.stripe.errors.oauthExchangeFailed',
  oauth_exchange_failed: 'agency.stripe.errors.oauthExchangeFailed',
  stripe_api_error: 'agency.stripe.errors.stripeApi',
  save_failed: 'agency.stripe.errors.saveFailed',
  profile_not_found: 'agency.stripe.errors.profileNotFound',
  invalid_body: 'agency.stripe.errors.generic',
  internal_error: 'agency.stripe.errors.generic',
};

export type StripeAccountStatus =
  | 'not_connected'
  | 'pending'
  | 'active'
  | 'restricted';

/**
 * Tipo di account Stripe Connect collegato.
 * Solo `express` ha l'Express Dashboard raggiungibile via login link: per `standard`
 * l'edge function restituisce direttamente l'URL di dashboard.stripe.com, dove
 * l'utente accede con le proprie credenziali Stripe.
 * `null` = non ancora rilevato (account collegati prima dell'introduzione del campo).
 */
export type StripeAccountType = 'express' | 'standard' | 'custom' | null;

type StatusResponse = {
  stripe_account_status: StripeAccountStatus;
  stripe_charges_enabled: boolean;
  stripe_account_type?: StripeAccountType;
} | null;

/**
 * Stato e azioni dell'account Stripe Connect dell'utente corrente.
 * Condiviso da StripeSection (onboarding completo) e AgencyPage (solo dashboard).
 */
export const useStripeAccount = () => {
  const { t } = useI18n();
  const { profileData, invalidateProfile } = useFullProfile();

  /**
   * Traduce l'errore di una edge function: messaggio specifico se il backend ha
   * mandato un codice noto, altrimenti generico. Il testo tecnico non finisce mai
   * a schermo ma resta in console per il debug.
   */
  const errorMessage = (err: unknown): string => {
    const code = err instanceof EdgeFunctionError ? err.code : undefined;
    const key = code ? ERROR_KEYS[code] : undefined;
    if (!key) {
      console.error('[stripe-connect]', err);
      return t('agency.stripe.errors.generic');
    }
    return t(key);
  };

  const [status, setStatus] = useState<StripeAccountStatus>('not_connected');
  const [chargesEnabled, setChargesEnabled] = useState(false);
  const [accountType, setAccountType] = useState<StripeAccountType>(null);

  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);

  useEffect(() => {
    const pd = profileData?.private_data;
    if (!pd) return;
    setStatus(pd.stripe_account_status || 'not_connected');
    setChargesEnabled(pd.stripe_charges_enabled || false);
    setAccountType(pd.stripe_account_type || null);
  }, [profileData]);

  const applyResult = (result: StatusResponse) => {
    if (!result) return;
    setStatus(result.stripe_account_status);
    setChargesEnabled(result.stripe_charges_enabled);
    if (result.stripe_account_type !== undefined) {
      setAccountType(result.stripe_account_type);
    }
    invalidateProfile();
  };

  /** Rilegge lo stato reale da Stripe e lo persiste (backfilla anche il tipo account). */
  const syncStatus = async () => {
    setSyncing(true);
    try {
      const result = await invokeEdgeFunction<StatusResponse>(supabase, 'stripe-connect-status', { body: {} });
      applyResult(result);

      if (result?.stripe_charges_enabled) {
        toast({ variant: 'success', title: t('agency.stripe.messages.accountActivated'), description: t('agency.stripe.messages.accountActivatedDesc') });
      } else if (result?.stripe_account_status === 'pending') {
        toast({ variant: 'warning', title: t('agency.stripe.messages.onboardingPending'), description: t('agency.stripe.messages.onboardingPendingDesc') });
      }
    } catch (err) {
      toast({ title: t('agency.stripe.messages.statusError'), description: errorMessage(err), variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  /** Avvia l'onboarding: `express` crea un account nuovo, `oauth` ne collega uno esistente. */
  const connect = async (mode: 'express' | 'oauth' = 'express') => {
    setConnecting(true);
    try {
      const origin = window.location.origin;
      const returnUrl = mode === 'oauth' ? `${origin}/` : `${origin}/?stripe_return=1`;
      const result = await invokeEdgeFunction<{ url?: string } | null>(supabase, 'stripe-connect-onboard', {
        body: { mode, return_url: returnUrl, refresh_url: `${origin}/?stripe_refresh=1` },
      });
      if (!result?.url) throw new EdgeFunctionError('missing url', { code: 'internal_error' });
      window.location.href = result.url;
    } catch (err) {
      toast({ title: t('agency.stripe.messages.connectError'), description: errorMessage(err), variant: 'destructive' });
      setConnecting(false);
    }
  };

  /** Completa il flusso OAuth scambiando il code ricevuto sul redirect. */
  const completeOAuth = async (code: string) => {
    setSyncing(true);
    try {
      const result = await invokeEdgeFunction<StatusResponse>(supabase, 'stripe-connect-oauth-callback', {
        body: { code },
      });
      if (result) {
        applyResult(result);
        toast({ variant: 'success', title: t('agency.stripe.messages.oauthConnected'), description: t('agency.stripe.messages.oauthConnectedDesc') });
      }
    } catch (err) {
      toast({ title: t('agency.stripe.messages.oauthError'), description: errorMessage(err), variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Apre la dashboard Stripe. L'edge function decide l'URL corretto in base al tipo
   * di account: login link (express) o dashboard.stripe.com (standard).
   */
  const openDashboard = async () => {
    setOpeningDashboard(true);
    try {
      const result = await invokeEdgeFunction<{ url?: string; account_type?: StripeAccountType } | null>(
        supabase, 'stripe-connect-login-link', { body: {} },
      );
      if (!result?.url) throw new EdgeFunctionError('missing url', { code: 'internal_error' });
      // La risposta è la fonte più aggiornata sul tipo account
      if (result.account_type) setAccountType(result.account_type);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({ title: t('agency.stripe.messages.dashboardError'), description: errorMessage(err), variant: 'destructive' });
    } finally {
      setOpeningDashboard(false);
    }
  };

  return {
    status,
    chargesEnabled,
    accountType,
    /** true quando l'account può effettivamente incassare */
    isActive: status === 'active' && chargesEnabled,
    /** gli account standard portano alla dashboard Stripe dell'utente, non alla nostra */
    isStandard: accountType === 'standard',
    connecting,
    syncing,
    openingDashboard,
    connect,
    syncStatus,
    completeOAuth,
    openDashboard,
  };
};
